import './DigitalAudit.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Page } from '@Components/Page';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { useSetError } from '../../redux/modals';
import { AuditReportView } from './components/AuditReportView';
import { ChannelHealthView } from './components/ChannelHealthView';
import { CommentsUrlContext } from './comments-context';
import { ConfigurationView } from './components/ConfigurationView';
import { ClientGuideView } from './components/ClientGuideView';
import { GlossaryView } from './components/GlossaryView';
import { SiteHealthView } from './components/SiteHealthView';
import { ExportMenu } from './components/ExportMenu';
import { JourneyView } from './components/JourneyView';
import { PixelIntelView } from './components/PixelIntelView';
import { ScanInputPanel } from './components/ScanInputPanel';
import { FullScanStatusBanner } from './components/FullScanStatusBanner';
import { ScanSourceBadge } from './components/ScanSourceBadge';
import { startFullScan } from './full-scan/full-scan-api';
import { useFullScanJobPolling } from './full-scan/use-full-scan-job';
import { FullScanStatus, hasUsableFullScanResult } from './full-scan/full-scan-constants';
import {
  AuditReport,
  ClientTypeType,
  DigitalAuditTab,
  DigitalAuditTabType,
  DIGITAL_AUDIT_NAVS,
  ScanInput,
  ScanSource
} from './digital-audit-constants';
import { EditableMilestone } from './engine/journey-mapper-constants';
import { fetchScanArtifacts } from './digital-audit-api';
import { createDomainConfig, resolveDomainConfig, updateDomainConfig } from './domain-config-api';
import {
  applyDomainConfigToReport,
  journeyToEditableMilestones,
  summarizeMilestoneChange
} from './journey-milestone-utils';
import { useInvalidateDomainConfig } from './use-domain-config';
import { createReportRun, getReportRun } from './report-runs-api';
import { runDetectionPipeline } from './engine/martech-scanner-utils';
import { mergeFullScanIntoScanResult } from './engine/full-scan-merge-utils';
import { buildJourney } from './engine/journey-mapper-utils';
import { extractTagFires } from './engine/tag-fire-utils';
import { summarizeRenderChecks } from './engine/site-health-utils';
import { buildDataLayerInventory } from './engine/datalayer-inventory-utils';

// Correlates a full-scan job that resolves before the quick-scan report it
// belongs to has been constructed yet - startFullScan and fetchScanArtifacts
// run in parallel with no ordering guarantee.
interface PendingFullScanJob {
  scanId: string;
  jobId: string;
}

const DigitalAudit = () => {
  const setError = useSetError();
  const invalidateDomainConfig = useInvalidateDomainConfig();
  const [activeTab, setActiveTab] = useState<DigitalAuditTabType>(DigitalAuditTab.SCAN);
  const activeNav = DIGITAL_AUDIT_NAVS.find(nav => nav.key === activeTab) ?? {
    key: activeTab,
    label: activeTab
  };
  const [report, setReport] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const currentScanIdRef = useRef<string | null>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const pendingFullScanJobRef = useRef<PendingFullScanJob | null>(null);
  // Wrapping the read in a function (rather than reading `.current` inline)
  // sidesteps a TS control-flow quirk where the assignment right above an
  // `if (alsoRunFullScan) {...}` block narrows this ref's type at every
  // later read in the same function, even past the intervening async call.
  const readPendingFullScanJob = (): PendingFullScanJob | null => pendingFullScanJobRef.current;

  const jobQuery = useFullScanJobPolling(report?.fullScanJobId ?? null);

  // Merge a full-scan job into the report exactly once, as soon as it
  // reaches either terminal outcome that carries real per-page evidence -
  // DONE or PARTIAL (a partial crawl still captured whatever pages it got to
  // before its deadline/page-cap, and that's worth showing rather than
  // discarding). Guarded on fullScanJobId (drops a stale, slow job from an
  // earlier scan) and scanSource (won't re-merge the same job twice).
  useEffect(() => {
    const job = jobQuery.data;
    const isMergeableStatus =
      job?.status === FullScanStatus.DONE || job?.status === FullScanStatus.PARTIAL;
    // DEV-24211: a terminal job can still carry a null/empty result (e.g. the
    // crawl's result upload failed); skip it rather than merging in nothing -
    // the banner treats this same "terminal but unusable" case as unmergeable
    // instead of spinning on "running" forever.
    if (!job || !isMergeableStatus || !hasUsableFullScanResult(job) || !report) {
      return;
    }
    if (report.fullScanJobId !== job.id || report.scanSource === ScanSource.FULL) {
      return;
    }
    const { result: fullScanResult } = job;
    const scanResult = mergeFullScanIntoScanResult(report.scanResult, fullScanResult);
    // DEV-24211: pass through the user's milestone edits (if any), or this
    // silently reverts the journey to the template on every full-scan merge.
    const journey = buildJourney(
      scanResult,
      report.input.clientType,
      report.input.vertical,
      report.editedMilestones ?? undefined,
      report.input.clientName
    );
    // A page's `errors` array holds non-fatal warnings (e.g. "consent
    // selector not found on this page") alongside true failures - most
    // pages log at least one even when they returned full evidence, so
    // `errors.length === 0` massively undercounts. evidenceBlobs.length
    // is the real signal: it's empty only when the page capture itself
    // never got off the ground (session/navigation totally failed).
    const fullScanPageCoverage = {
      pagesCaptured: fullScanResult.pages.filter(page => page.evidenceBlobs.length > 0).length,
      pagesTotal: fullScanResult.pages.length
    };
    const mergedReport: AuditReport = {
      ...report,
      scanResult,
      journey,
      scanSource: ScanSource.FULL,
      fullScanPageCoverage
    };
    setReport(mergedReport);

    // DEV-24213: a full-scan merge upgrades the report in place, but its
    // findings are meaningfully different from the quick scan's (a
    // different page set, real-browser evidence) - save it as its own,
    // second report run rather than overwriting the quick scan's saved run,
    // so a domain's history stays honest about what each run actually saw.
    createReportRun({
      url: mergedReport.input.url,
      scan_source: mergedReport.scanSource,
      report: mergedReport
    }).catch(e =>
      setError({ message: `Couldn't save the full-scan report run: ${e.message}`, reportError: e })
    );
  }, [jobQuery.data, report, setError]);

  const handleScan = useCallback(
    async (input: ScanInput, alsoRunFullScan: boolean) => {
      setLoading(true);
      const thisScanId = crypto.randomUUID();
      currentScanIdRef.current = thisScanId;
      pendingFullScanJobRef.current = null;

      // Fired independently of the static scan below, same as startFullScan -
      // ensures the canonical domain-config row exists for a first-time
      // domain. createReportRun (below, once the report is built) requires
      // this to have already succeeded, so its own .then chains off this
      // promise rather than re-awaiting it inline here.
      const domainConfigPromise = createDomainConfig({
        url: input.url,
        client_type: input.clientType,
        vertical: input.vertical,
        ...(input.clientName ? { client_name: input.clientName } : {})
      })
        .then(result => {
          // The scan panel cached a 404 for this URL while the user typed it;
          // refresh so the lock and Configuration tab see the new row.
          invalidateDomainConfig();
          return result;
        })
        .catch(e => {
          setError({ message: `Couldn't save the domain config: ${e.message}`, reportError: e });
          return null;
        });

      // Fired independently of the static scan below — it's a separate async
      // job (see full-scan/), not something the report/journey build depends on.
      if (alsoRunFullScan) {
        startFullScan(input.url, input.consentSelector)
          .then(({ jobId }) => {
            if (currentScanIdRef.current !== thisScanId) {
              return; // a newer scan superseded this one before the job started
            }
            setReport(prev => {
              if (prev && prev.scanId === thisScanId) {
                return { ...prev, fullScanJobId: jobId };
              }
              // The quick-scan report hasn't been constructed yet - stash the
              // job id for handleScan to pick up below.
              pendingFullScanJobRef.current = { scanId: thisScanId, jobId };
              return prev;
            });
          })
          .catch(e =>
            setError({ message: `Couldn't start full site scan: ${e.message}`, reportError: e })
          );
      }

      try {
        const artifacts = await fetchScanArtifacts(input.url);
        if (currentScanIdRef.current !== thisScanId) {
          return; // a newer scan superseded this one
        }
        const pipeline = runDetectionPipeline(
          artifacts.evidenceBlobs,
          artifacts.scriptSrcs,
          artifacts.domain
        );
        const tagFires = extractTagFires(artifacts.evidenceBlobs);
        const scanResult = {
          url: input.url,
          domain: artifacts.domain,
          scannedAt: new Date().toISOString(),
          // Fallback covers the deploy window where the backend hasn't shipped
          // the multi-page crawl yet and omits pagesScanned.
          pagesScanned: artifacts.pagesScanned ?? [artifacts.finalUrl],
          jsFilesScanned: artifacts.evidenceBlobs.filter(b => b.source.startsWith('js:')).length,
          ...pipeline,
          tagFires,
          // No liveSnapshots arg - the quick static scan never executes JS,
          // so only the dataLayer.push()-text and GTM-container halves apply.
          dataLayerVariables: buildDataLayerInventory(artifacts.evidenceBlobs, tagFires),
          pageMetrics: artifacts.pageMetrics,
          discoverability: artifacts.discoverability,
          renderSummary: summarizeRenderChecks(artifacts.evidenceBlobs),
          errors: artifacts.errors
        };
        // The canonical config wins over what this scan was started with: it
        // carries the domain's locked client type/vertical and any milestone
        // edits saved by earlier scans. (domainConfigPromise never rejects -
        // a failure is reported above and resolves to null.)
        const savedConfig = (await domainConfigPromise)?.config;
        const reportInput: ScanInput = savedConfig
          ? { ...input, clientType: savedConfig.client_type, vertical: savedConfig.vertical }
          : input;
        const savedMilestones = savedConfig?.edited_milestones ?? null;
        const journey = buildJourney(
          scanResult,
          reportInput.clientType,
          reportInput.vertical,
          savedMilestones ?? undefined,
          reportInput.clientName
        );
        const pendingJob = readPendingFullScanJob();
        const fullScanJobId =
          pendingJob && pendingJob.scanId === thisScanId ? pendingJob.jobId : null;
        const newReport: AuditReport = {
          scanId: thisScanId,
          input: reportInput,
          scanResult,
          journey,
          scanSource: ScanSource.QUICK,
          fullScanJobId,
          quickScanSiteHealth: {
            pageMetrics: artifacts.pageMetrics,
            discoverability: artifacts.discoverability
          },
          fullScanPageCoverage: null,
          editedMilestones: savedMilestones
        };
        setReport(newReport);
        setActiveTab(DigitalAuditTab.REPORT);

        // Fire-and-forget, same as domainConfigPromise/startFullScan above -
        // a failed save shouldn't block the user from seeing their
        // just-completed scan. Chained off domainConfigPromise so this only
        // runs once that prerequisite has actually succeeded.
        domainConfigPromise
          .then(domainConfigResult => {
            if (!domainConfigResult) return undefined; // already reported above; nothing to attach this run to
            return createReportRun({
              url: input.url,
              scan_source: newReport.scanSource,
              report: newReport
            });
          })
          .catch(e =>
            setError({ message: `Couldn't save this report run: ${e.message}`, reportError: e })
          );
      } catch (e) {
        if (currentScanIdRef.current === thisScanId) {
          // DEV-24211: an AuditReport can't exist without a successful quick
          // scan (every tab reads scanResult/journey unconditionally), so a
          // full-scan job requested alongside it has nowhere to attach if the
          // quick scan fails - it keeps running server-side with no report to
          // merge into. `alsoRunFullScan` (not the not-yet-settled job-id ref,
          // which races this catch) is the reliable signal that a job was
          // requested; its own .catch above covers the case where it also
          // failed to start.
          const fullScanNote = alsoRunFullScan
            ? " A full site scan was also started for this URL and is still running in the background, but its results won't be shown since the quick scan failed — re-run the scan to see them."
            : '';
          setError({
            message: `${e.message ?? 'Scan failed. Please try again.'}${fullScanNote}`,
            reportError: e
          });
        }
      } finally {
        if (currentScanIdRef.current === thisScanId) {
          setLoading(false);
        }
      }
    },
    [setError, invalidateDomainConfig]
  );

  // "Use last findings" - skip the scan entirely and hydrate the report from
  // a previously-saved run instead. currentScanIdRef is bumped too, so a
  // still-in-flight handleScan from before this click can't clobber it.
  const handleReuseLastFindings = useCallback(
    async (runId: string) => {
      const thisScanId = crypto.randomUUID();
      currentScanIdRef.current = thisScanId;
      pendingFullScanJobRef.current = null;
      try {
        const { report: savedReport } = await getReportRun(runId);
        // The saved report's own scanId is foreign to this session (it was
        // generated the last time this domain was scanned) - mint a fresh one,
        // matching how handleScan does for a brand-new scan.
        // The saved run's own client type/vertical/milestones are as of when
        // it ran - re-align to the canonical config, which may have changed
        // since. If the lookup fails, the saved report is still valid as-is.
        const resolved = await resolveDomainConfig(savedReport.input.url).catch(() => null);
        const hydrated = resolved
          ? applyDomainConfigToReport(savedReport, resolved.config)
          : savedReport;
        setReport({ ...hydrated, scanId: thisScanId });
        setActiveTab(DigitalAuditTab.REPORT);
      } catch (e) {
        setError({ message: `Couldn't load the saved report: ${e.message}`, reportError: e });
      }
    },
    [setError]
  );

  // Re-align the finished scan to the journey (context change or milestone
  // edits) — the client-side equivalent of the reference tool's /api/journey.
  // Exports read report.journey, so they reflect edits automatically.
  const handleJourneyRemap = useCallback(
    (clientType: ClientTypeType, vertical: string, milestones?: EditableMilestone[]) => {
      setReport(prev => {
        if (!prev) return prev;
        const journey = buildJourney(
          prev.scanResult,
          clientType,
          vertical,
          milestones,
          prev.input.clientName
        );
        return {
          ...prev,
          input: { ...prev.input, clientType, vertical },
          journey,
          // DEV-24211: persist the raw edit list (not just the rebuilt
          // journey) so a later full-scan merge can pass it back into
          // buildJourney instead of reverting to the template. Every caller
          // that passes `milestones: undefined` ("reset to template" in
          // JourneyView.tsx) means it as a deliberate discard, so this must
          // clear to null, not
          // fall back to the previous edit list, or a later merge would
          // silently reapply edits the user just discarded.
          editedMilestones: milestones ?? null
        };
      });
    },
    []
  );

  // Canonical milestone persistence. Writes are serialized (each one reads the
  // fresh `updated_at` for its optimistic-concurrency check, so two in flight
  // at once would conflict with each other).
  const milestonePersistChainRef = useRef<Promise<void>>(Promise.resolve());
  const persistMilestones = useCallback(
    (url: string, milestones: EditableMilestone[] | null, summary: string) => {
      milestonePersistChainRef.current = milestonePersistChainRef.current.then(async () => {
        try {
          const resolved = await resolveDomainConfig(url);
          if (!resolved) return;
          await updateDomainConfig(resolved.domain, {
            edited_milestones: milestones,
            summary,
            expected_updated_at: resolved.config.updated_at
          });
          Mixpanel.track(MxE.RecordSaved, { page: 'Digital Audit', entity: 'journey milestones' });
          await invalidateDomainConfig();
        } catch (e) {
          setError({
            message: `Couldn't save your milestone changes for everyone: ${e.message}`,
            reportError: e
          });
        }
      });
    },
    [invalidateDomainConfig, setError]
  );

  // A Journey-tab milestone edit: apply it to this session's report, then make
  // it canonical for the domain (and audit-logged) - after the state update,
  // since handleJourneyRemap is a pure setReport updater.
  const handleJourneyEdit = useCallback(
    (clientType: ClientTypeType, vertical: string, milestones?: EditableMilestone[]) => {
      if (!report) return;
      const previous = report.editedMilestones ?? journeyToEditableMilestones(report.journey);
      const hadEdits = report.editedMilestones !== null;
      handleJourneyRemap(clientType, vertical, milestones);
      // Resetting a journey that has no saved edits changes nothing canonical.
      if (!milestones && !hadEdits) return;
      persistMilestones(
        report.input.url,
        milestones ?? null,
        summarizeMilestoneChange(previous, milestones ?? null)
      );
    },
    [report, handleJourneyRemap, persistMilestones]
  );

  return (
    <Page
      pageType='Digital Audit'
      title='Digital Audit'
      navs={DIGITAL_AUDIT_NAVS}
      selectedNav={activeTab}
      onNav={key => setActiveTab(key as DigitalAuditTabType)}
      actions={
        report && activeTab !== DigitalAuditTab.SCAN ? (
          <>
            <ScanSourceBadge
              scanSource={report.scanSource}
              pageCoverage={report.fullScanPageCoverage}
            />
            <ExportMenu report={report} tab={activeNav} tabContentRef={tabContentRef} />
          </>
        ) : undefined
      }
    >
      <CommentsUrlContext.Provider value={report?.input.url}>
        {report?.fullScanJobId && activeTab !== DigitalAuditTab.SCAN && (
          <FullScanStatusBanner
            job={jobQuery.data}
            merged={report.scanSource === ScanSource.FULL}
            partial={jobQuery.data?.status === FullScanStatus.PARTIAL}
            error={jobQuery.error}
            stalled={jobQuery.isStalled}
            onRetry={jobQuery.retry}
          />
        )}
        {activeTab === DigitalAuditTab.SCAN && (
          <div className='digital-audit-page'>
            <ScanInputPanel
              onScan={handleScan}
              onReuseLastFindings={handleReuseLastFindings}
              loading={loading}
            />
          </div>
        )}
        <div className='digital-audit-tab-capture' ref={tabContentRef}>
          {activeTab === DigitalAuditTab.REPORT && <AuditReportView report={report} />}
          {activeTab === DigitalAuditTab.SITE_HEALTH && <SiteHealthView report={report} />}
          {activeTab === DigitalAuditTab.PIXEL_INTEL && <PixelIntelView report={report} />}
          {activeTab === DigitalAuditTab.CHANNEL_HEALTH && <ChannelHealthView report={report} />}
          {activeTab === DigitalAuditTab.JOURNEY && (
            <JourneyView report={report} onRemap={handleJourneyEdit} />
          )}
          {activeTab === DigitalAuditTab.CONFIGURATION && (
            <ConfigurationView
              report={report}
              // Keep the user's Journey-tab milestone edits across a config change.
              onConfigSaved={(clientType, vertical) =>
                handleJourneyRemap(clientType, vertical, report?.editedMilestones ?? undefined)
              }
            />
          )}
          {activeTab === DigitalAuditTab.CLIENT_GUIDE && <ClientGuideView report={report} />}
          {activeTab === DigitalAuditTab.GLOSSARY && <GlossaryView />}
        </div>
      </CommentsUrlContext.Provider>
    </Page>
  );
};

export { DigitalAudit };
